import React, { useState, useCallback, useMemo } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';

const FontLoader = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&display=swap');
  body {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: ${p => p.theme.bg};
  }
`;

import PasswordStep from './PasswordStep';
import OtpStep from './OtpStep';
import TezLabLogo from '../../shared/TezLabLogo';
import SideAppLogo, { SideAppFullLogo } from '../../shared/SideAppLogo';
import buildThemeFromBranding from '../../shared/brandingTheme';

// Server-rendered "Sign in with Rivian" webview. Same scaffold as the Tesla
// SSO flows (OAuthApp / SsoClaimApp) so every TezLab sign-in surface looks
// identical; only the steps differ (Rivian is credentials → optional MFA).
// The backend (RivianOauthController) drives Rivian's API and returns JSON;
// on completion it hands back the app redirect_uri (a PKCE code) which we
// navigate to.
const STEPS = { PASSWORD: 'password', OTP: 'otp', COMPLETE: 'complete', ERROR: 'error' };

const Page = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.pageBg};
  color: ${p => p.theme.text};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  padding-bottom: calc(24px + 10vh);
  font-family: ${p => p.theme.fontFamily};
`;

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.div`
  margin-bottom: 14px;
`;

const LogoBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 28px;
`;

const Card = styled.div`
  width: 100%;
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radiusLg};
  padding: 40px;

  @media (max-width: 640px) {
    max-width: 100%;
    padding: 24px;
  }
`;

const PoweredBy = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: ${p => p.theme.textTertiary};
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.7;

  svg { opacity: 0.9; }
`;

const AppName = styled.div`
  color: ${p => p.theme.text};
  font-size: 18px;
  font-weight: 600;
  margin-top: 12px;
  margin-bottom: 6px;
  text-align: center;
`;

const Heading = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: ${p => p.theme.text};
  margin-bottom: 12px;
  text-align: center;
`;

const Message = styled.div`
  text-align: center;
  color: ${p => p.theme.textSecondary};
  font-size: 15px;
  line-height: 24px;
`;

export default function RivianApp(props) {
  const { branding, csrfToken, error: initialError } = props;

  const theme = useMemo(function () { return buildThemeFromBranding(branding); }, [branding]);

  const [step, setStep] = useState(initialError ? STEPS.ERROR : STEPS.PASSWORD);
  const [email, setEmail] = useState('');
  const [errorMessage] = useState(initialError || null);
  // MFA channel context from the login response: which method the code went to
  // and the full list the user can switch between (see OtpStep's picker).
  const [otpContext, setOtpContext] = useState({ targetChannel: null, channels: [] });

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken,
  }), [csrfToken]);

  // Both steps resolve the flow the same way: the server returns
  // { status: 'complete', redirect_to } and we navigate the webview there
  // (a PKCE authorization-code URL the app captures).
  const onComplete = useCallback(function (redirectTo) {
    setStep(STEPS.COMPLETE);
    if (redirectTo) window.location = redirectTo;
  }, []);

  const onOtpRequired = useCallback(function (context) {
    if (context) setOtpContext({ targetChannel: context.targetChannel || null, channels: context.channels || [] });
    setStep(STEPS.OTP);
  }, []);

  const renderStep = function () {
    switch (step) {
      case STEPS.PASSWORD:
        return (
          <PasswordStep
            email={email}
            setEmail={setEmail}
            headers={headers}
            onOtpRequired={onOtpRequired}
            onComplete={onComplete}
          />
        );
      case STEPS.OTP:
        return (
          <OtpStep
            headers={headers}
            onComplete={onComplete}
            targetChannel={otpContext.targetChannel}
            channels={otpContext.channels}
            onTargetChange={(channel) => setOtpContext((prev) => ({ ...prev, targetChannel: channel }))}
          />
        );
      case STEPS.COMPLETE:
        return (
          <div>
            <Heading>You&apos;re all set!</Heading>
            <Message>Redirecting you back to the app&hellip;</Message>
          </div>
        );
      case STEPS.ERROR:
        return (
          <div>
            <Heading>Something went wrong</Heading>
            <Message>{errorMessage}</Message>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <FontLoader />
      <Page>
        <Container>
          <LogoBlock>
            {branding && branding.fullLogoKey ? (
              <Logo><SideAppFullLogo fullLogoKey={branding.fullLogoKey} /></Logo>
            ) : (
              <>
                <Logo>
                  {branding && (branding.logoImageUrl || branding.logoKey)
                    ? <SideAppLogo logoImageUrl={branding.logoImageUrl} logoKey={branding.logoKey} alt={branding.appName} />
                    : <TezLabLogo />}
                </Logo>
                {branding && branding.showPoweredBy && branding.appName && (
                  <AppName>{branding.appName}</AppName>
                )}
                {branding && branding.showPoweredBy && (
                  <PoweredBy>
                    <span>powered by</span>
                    <TezLabLogo height={22} fill={theme.textTertiary} />
                  </PoweredBy>
                )}
              </>
            )}
          </LogoBlock>
          <Card>{renderStep()}</Card>
        </Container>
      </Page>
    </ThemeProvider>
  );
}
